import { cloneElement, ReactElement, ReactNode } from 'react';

import styled from 'styled-components';

import { Badge, BadgeVariant } from '@components/badge';
import { IconProps } from '@components/icons/base';
import { IconErrorSmall } from '@components/icons/error-small';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

export interface TabDisplay {
  label: string;
  counter?: number;
  /**
   * Badge color for the counter. Defaults to `neutral`. Use `green` for Hire's
   * "new activity" indicator, `blue` for Journeys' "search hits on other tabs".
   */
  counterVariant?: BadgeVariant;
  isCounterSolid?: boolean;
  hasError?: boolean;
  icon?: ReactElement<IconProps>;
  dataTestId?: string;
}

export interface BaseTab extends TabDisplay {
  content: ReactNode;
}

export type TabActiveProps = {
  $active: boolean;
};

export const Label = styled(Text).attrs({ font: 'semibold14' })<TabActiveProps>(({ $active }) => ({
  color: $active ? theme.color.textPrimary : theme.color.textHelper,
  transition: transitionStandard('color'),
  whiteSpace: 'nowrap',
}));

export const TAB_ACTIVE_BORDER_HEIGHT = pxToRem(4);

export const TabActiveBorder = styled.div<TabActiveProps>(({ $active }) => ({
  backgroundColor: $active ? theme.color.elementIndigo100 : 'transparent',
  width: '100%',
  height: TAB_ACTIVE_BORDER_HEIGHT,
  transition: transitionStandard('all'),
  transform: $active ? 'scaleX(1) translateX(0px)' : 'scaleX(0) translateX(50%)',
  borderTopLeftRadius: pxToRem(2),
  borderTopRightRadius: pxToRem(2),
}));

export type TabContentProps = {
  tab: TabDisplay;
  isActive: boolean;
};

export const TabContent = ({ tab, isActive }: TabContentProps) => {
  return (
    <Stack space={4}>
      <Stack space={1} orientation="horizontal" align="center" justify="center">
        {tab.icon &&
          cloneElement(tab.icon, {
            ...tab.icon.props,
            size: 16,
            color: isActive ? 'textPrimary' : 'textHelper',
          })}
        <Label $active={isActive} text={tab.label} />
        {tab.counter !== undefined && (
          <Badge text={tab.counter} variant={tab.counterVariant} isSolid={tab.isCounterSolid} />
        )}
        {tab.hasError && <IconErrorSmall color="red100" className="tab-status-icon" />}
      </Stack>
      <TabActiveBorder $active={isActive} />
    </Stack>
  );
};

export const baseTabStyles = ({ $active }: TabActiveProps) => ({
  marginRight: pxToRem(24),
  flexShrink: 0,
  position: 'relative' as const,
  color: $active ? theme.color.textPrimary : theme.color.textHelper,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer' as const,
  outline: 'inherit',
  textDecoration: 'none',
  borderRadius: pxToRem(2),
  ':focus-visible': {
    '--shadow-focus-color': theme.color.border100,
    boxShadow: theme.shadow.focus,
  },
  ...(!$active && {
    ':hover': {
      cursor: 'pointer',
      color: theme.color.textSecondary,
      [`${TabActiveBorder}`]: {
        backgroundColor: theme.color.uiTrans20,
        transform: 'scaleX(1) translateX(0px)',
      },
      [`${Label}`]: {
        color: theme.color.textSecondary,
      },
      // Recolor the leading icon to follow the label, but leave status icons
      // (e.g. the red error indicator) at their semantic color.
      'svg:not(.tab-status-icon)': {
        color: theme.color.textSecondary,
      },
    },
  }),
});
